import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * UID-603 (ADR-0176 D1.4) — the ONE shared cursor-append pagination hook for
 * every list read that adopts the `{ items, nextCursor, totalCount }`
 * envelope (`GET /catalog/search`, `GET /rfq`, `GET /parts`). Modeled
 * directly on `useCatalogSearch.ts`'s `loadMore()`/`isLoadingMore`/
 * `loadMoreError` split — a failed append keeps the already-loaded rows on
 * screen rather than collapsing to a full-page error. Per ADR-0176 D1.4,
 * this repo has no react-query / `useInfiniteQuery` — this hook is the
 * `openapi-fetch` + `useState`/`useEffect` equivalent, and no new
 * data-fetching library is introduced here either.
 *
 * `TPage` is deliberately generic (not hardcoded to `{items,nextCursor,
 * totalCount}`) so a caller can carry endpoint-specific page-independent
 * fields alongside the envelope — e.g. RFQ's `statusCounts` (ADR-0176 D5) or
 * Parts' `counts`/`boms` (D1.3) — without this hook knowing their names.
 * Those extra fields are only ever sent on the FIRST page (cursor absent,
 * D4/D5) — `loadMore`'s merge preserves whatever the previous page already
 * had for a key a later page omits, rather than clobbering it with
 * `undefined`.
 */
export interface CursorPage<TItem> {
  readonly items: readonly TItem[]
  readonly nextCursor: string | null
  readonly totalCount?: number
}

/**
 * M2 (code-review UID-603 fourth pass) — a `fetchPage` implementation throws
 * this (instead of a plain `Error`) when the server rejected the CURSOR
 * ITSELF as unresolvable (a deliberate 400 — e.g. `rfq-list-cursor.ts`'s
 * `unknownRfqCursorAnchor`/malformed-token cases; `library-store.ts` has the
 * equivalent for parts). That cursor can never succeed: `loadMore` always
 * re-reads `state.page.nextCursor`, so retrying via `loadMore` re-issues the
 * SAME dead cursor forever. A caller's retry affordance should check
 * `loadMoreErrorIsCursorRejection` below and call `reload()` (reset to page
 * 1, which mints a fresh cursor) instead of `loadMore()` when it's set.
 */
export class CursorRejectedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CursorRejectedError'
  }
}

export type CursorListState<TPage> =
  /** Not yet fetched — `enabled` is false (a not-yet-visited tab, UID-603 D6
   * row 2's per-tab lazy fetch) and no page has ever loaded. */
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | {
      readonly status: 'ready'
      readonly page: TPage
      /** True while a "load more" (cursor-append) request is in flight.
       * Distinct from `status: 'loading'`, which REPLACES the list (a fresh
       * load/filter change) — appending never hides already-loaded rows. */
      readonly isLoadingMore: boolean
      /** Set when a "load more" request fails; the already-loaded page
       * stays on screen. Cleared on the next successful append or reload. */
      readonly loadMoreError: string | null
      /** M2 (fourth pass) — true when `loadMoreError` came from a
       * {@link CursorRejectedError} (the current `nextCursor` is dead, not a
       * transient network/500 failure). A retry affordance should call
       * `reload()`, not `loadMore()`, when this is true — see
       * {@link CursorRejectedError}'s doc comment. Always `false` when
       * `loadMoreError` is `null`. */
      readonly loadMoreErrorIsCursorRejection: boolean
    }

export interface UseCursorListOptions<TItem> {
  /** Stable identity extractor for cross-page dedupe (ADR-0176 D1.5) — e.g.
   * `rfq.id`, `part.sourceable.id`. Required: D2.2's `updated_at` churn can
   * otherwise move a row across the page boundary and render it twice. */
  readonly getId: (item: TItem) => string
  readonly pageSize: number
  /** False keeps the hook in `idle` and fetches nothing — the per-tab lazy
   * fetch (only load a tab's first page once it's actually visited). Once
   * true, stays fetched; toggling back to false does NOT clear the loaded
   * page (a revisited tab shows its cache, not a spinner). */
  readonly enabled: boolean
  /** Changing any entry (by `Object.is`) resets to page 1 and re-fetches —
   * e.g. the active filter/search term/sort. Only checked while `enabled`. */
  readonly resetKey: readonly unknown[]
}

export interface UseCursorListResult<TPage> {
  readonly state: CursorListState<TPage>
  /** Advances via the current page's `nextCursor`, appending onto the
   * already-loaded items. No-op when there's no next page, a load-more is
   * already in flight, or the list hasn't loaded yet — safe to call
   * unconditionally from a scroll/intersection handler. Guarded by an
   * in-flight ref (not just `isLoadingMore` state) so rapid scroll events
   * firing within the same tick — before a state update has re-rendered —
   * can never issue two overlapping requests for the same cursor. */
  readonly loadMore: () => void
  /** Re-runs the first page with the current inputs (retry-after-error, or
   * an explicit refresh). */
  readonly reload: () => void
}

function dedupeAppend<TItem>(
  existing: readonly TItem[],
  incoming: readonly TItem[],
  getId: (item: TItem) => string,
): readonly TItem[] {
  const seen = new Set(existing.map(getId))
  const additions = incoming.filter((item) => !seen.has(getId(item)))
  return additions.length === 0 ? existing : [...existing, ...additions]
}

export function useCursorList<TItem, TPage extends CursorPage<TItem>>(
  fetchPage: (opts: { readonly cursor?: string; readonly limit: number }) => Promise<TPage>,
  options: UseCursorListOptions<TItem>,
): UseCursorListResult<TPage> {
  const { getId, pageSize, enabled, resetKey } = options
  const [state, setState] = useState<CursorListState<TPage>>({ status: 'idle' })
  const [generation, setGeneration] = useState(0)
  const fetchPageRef = useRef(fetchPage)
  fetchPageRef.current = fetchPage
  const getIdRef = useRef(getId)
  getIdRef.current = getId
  // W3 (code-review UID-603) — a plain render-time mirror of `state`, the
  // SAME non-effect ref-sync convention `fetchPageRef`/`getIdRef` above
  // already use. `loadMore` reads this synchronously to decide whether to
  // fetch, so the actual network call and the `loadMoreInFlightRef` guard
  // live in `loadMore`'s own function body — never inside a `setState`
  // updater. Updater functions must stay pure; the previous revision fired
  // the fetch (a side effect) from inside one, which is also why, under
  // React StrictMode's double-invoke of updaters, `isLoadingMore` could fail
  // to ever flip `true` in dev.
  const stateRef = useRef(state)
  stateRef.current = state
  // In-flight guard, separate from `isLoadingMore` state: two `loadMore()`
  // calls issued back-to-back (e.g. two scroll/intersection events firing
  // synchronously before a re-render commits) would both read the same
  // stale `stateRef.current.isLoadingMore: false` without this ref. This is
  // the hard dedup-fetch requirement (ADR-0176 D1.4/D1.6) — never a "nice to
  // have."
  const loadMoreInFlightRef = useRef(false)
  // W2 (code-review UID-603) — bumped every time the first-page effect below
  // actually issues a fetch (a `resetKey`/`generation`/`enabled` change).
  // `loadMore` captures the CURRENT epoch when it starts a request and
  // compares it again when that request resolves: if `resetKey` changed
  // (search debounce, sort toggle, `refetch()`) while the load-more was in
  // flight, the epoch will have moved on and the stale response is
  // discarded rather than merged into the new page-1 result — otherwise the
  // old page's rows/`nextCursor` could corrupt the new result set.
  const requestEpochRef = useRef(0)
  // W3 (code-review UID-603 second pass) — `true` for the whole window a
  // page-1 refetch triggered by a `resetKey`/`generation`/`enabled` change
  // is in flight. That window deliberately leaves the OLD `ready` page (and
  // its OLD `nextCursor`) on screen while the new page 1 loads (see the
  // `setState` below), which means `stateRef.current` still looks like a
  // perfectly normal "ready, has more" list to `loadMore` for that whole
  // window. Worse, `requestEpochRef` is bumped SYNCHRONOUSLY at the top of
  // this effect, before the fetch even starts — so a `loadMore()` call
  // during that window captures the ALREADY-bumped new epoch, and the W2
  // epoch guard in `loadMore`'s `.then`/`.catch` (below) never sees a
  // mismatch and never discards it. The result: a `loadMore()` fired in
  // that narrow window fetches page 2 for the OLD filter/cursor and merges
  // it into whatever the NEW filter's page 1 eventually resolves to. This
  // ref closes that window directly — `loadMore` early-returns while it's
  // `true`, independent of the epoch check.
  const pageOneInFlightRef = useRef(false)

  // First-page load / reload — depends on `enabled` and the caller's
  // `resetKey` entries, plus the manual `generation` bump from `reload()`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is a caller-controlled array spread intentionally below; fetchPage/getId are read via refs.
  useEffect(() => {
    if (!enabled) return
    requestEpochRef.current += 1
    const epoch = requestEpochRef.current
    pageOneInFlightRef.current = true
    let cancelled = false
    setState((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }))
    fetchPageRef
      .current({ limit: pageSize })
      .then((page) => {
        if (!cancelled)
          setState({
            status: 'ready',
            page,
            isLoadingMore: false,
            loadMoreError: null,
            loadMoreErrorIsCursorRejection: false,
          })
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Something went wrong. Try again.',
          })
        }
      })
      .finally(() => {
        // Only clear the flag if THIS is still the most recent request — a
        // later `resetKey` change may have already bumped the epoch again
        // and started a newer page-1 fetch that is still genuinely in
        // flight; that request's own `.finally` is what should clear it.
        if (requestEpochRef.current === epoch) {
          pageOneInFlightRef.current = false
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pageSize, generation, ...resetKey])

  const loadMore = useCallback(() => {
    const current = stateRef.current
    if (
      current.status !== 'ready' ||
      current.page.nextCursor === null ||
      current.isLoadingMore ||
      loadMoreInFlightRef.current ||
      pageOneInFlightRef.current
    ) {
      return
    }
    loadMoreInFlightRef.current = true
    const cursor = current.page.nextCursor
    const requestEpoch = requestEpochRef.current
    setState((latest) =>
      latest.status === 'ready'
        ? {
            ...latest,
            isLoadingMore: true,
            loadMoreError: null,
            loadMoreErrorIsCursorRejection: false,
          }
        : latest,
    )
    fetchPageRef
      .current({ cursor, limit: pageSize })
      .then((newPage) => {
        setState((latest) => {
          if (latest.status !== 'ready') return latest
          // Stale — `resetKey`/`generation` moved on while this request was
          // in flight (W2). Discard rather than merge into the new page 1.
          if (requestEpochRef.current !== requestEpoch) return latest
          const mergedItems = dedupeAppend(latest.page.items, newPage.items, getIdRef.current)
          return {
            status: 'ready',
            // Preserve any page-independent extra field (statusCounts,
            // counts, …) the later page omitted (D4/D5: issued only when
            // `cursor` is absent) — spread the prior page first, THEN the
            // new page (advances `nextCursor` and any field the new page
            // DID send), then force `items` to the deduped merge.
            page: { ...latest.page, ...newPage, items: mergedItems },
            isLoadingMore: false,
            loadMoreError: null,
            loadMoreErrorIsCursorRejection: false,
          }
        })
      })
      .catch((error: unknown) => {
        setState((latest) => {
          if (latest.status !== 'ready') return latest
          if (requestEpochRef.current !== requestEpoch) return latest
          // M2 (fourth pass) — `CursorRejectedError` means `cursor` (the
          // value just captured above, still `latest.page.nextCursor`) is
          // dead and will never succeed via another `loadMore()`; flag it so
          // the caller's retry affordance can `reload()` instead.
          return {
            ...latest,
            isLoadingMore: false,
            loadMoreError:
              error instanceof Error ? error.message : 'Couldn’t load more. Try again.',
            loadMoreErrorIsCursorRejection: error instanceof CursorRejectedError,
          }
        })
      })
      .finally(() => {
        loadMoreInFlightRef.current = false
      })
  }, [pageSize])

  const reload = useCallback(() => setGeneration((g) => g + 1), [])

  return { state, loadMore, reload }
}
